import { en as voiceCallsEn, de as voiceCallsDe } from './adminVoiceCallMessages';
import { en as optimizerEn, de as optimizerDe } from './adminOptimizerMessages';
import React from 'react';
import { en as usersEn, de as usersDe } from './adminUsersMessages';
import { en as shellEn, de as shellDe } from './adminShellMessages';
import { en as communitiesEn, de as communitiesDe } from './adminCommunityMessages';
import { en as rideFiltersEn, de as rideFiltersDe } from './adminRideFilterMessages';

type Language = 'en' | 'de';

type I18nContextValue = {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, values?: Record<string, string | number>) => string;
};

const I18nContext = React.createContext<I18nContextValue | undefined>(undefined);

const en: Record<string, string> = {
  "bookings.tabs": "Ride operations",
  "bookings.tab": "Bookings",
  "bookings.title": "Bookings",
  "bookings.total": "Total bookings",
  "bookings.description": "Track acceptance, start, pickup and dropoff for all bookings.",
  "bookings.detailTitle": "Booking lifecycle",
  "bookings.timeline": "Lifecycle timeline",
  "bookings.noEvents": "No lifecycle steps recorded.",
  "bookings.unknownActor": "Person not recorded",
  "bookings.historical": "Historical timestamp",
  "bookings.offerCreated": "Booking created from driver offer",
  "bookings.view": "View",
  "bookings.view.today": "Today",
  "bookings.view.open": "All open",
  "bookings.view.all": "All",
  "bookings.view.range": "Date range",
  "bookings.search": "Search",
  "bookings.searchPlaceholder": "Booking / ride ID, name or email",
  "bookings.phaseLabel": "Current step",
  "bookings.anyPhase": "All steps",
  "bookings.pageSize": "Rows",
  "bookings.from": "From",
  "bookings.to": "Through",
  "bookings.invalidRange": "Start must be on or before the end.",
  "bookings.booking": "Booking / ride",
  "bookings.requester": "Requested by",
  "bookings.driver": "Driver",
  "bookings.route": "Pickup → dropoff",
  "bookings.scheduled": "Scheduled ride",
  "bookings.lastActivity": "Last activity",
  "bookings.lastSuccess": "Last successful refresh:",
  "bookings.loadFailed": "Failed to load bookings.",
  "bookings.retry": "Retry",
  "bookings.ride": "Open ride",
  "bookings.lifecycle": "Booking lifecycle",
  "bookings.openTimeline": "Open timeline",
  "bookings.closeTimeline": "Close timeline",
  "bookings.exactFilter": "Exact booking:",
  "bookings.effect.DERIVED": "Automatically derived",
  "bookings.effect.BACKFILLED": "backfilled by admin",
  "bookings.effect.DIRECT": "Directly confirmed",
  "bookings.phase.WAITING_ACCEPTANCE": "Waiting for acceptance",
  "bookings.phase.WAITING_START": "Waiting for ride start",
  "bookings.phase.WAITING_PICKUP": "Waiting for pickup",
  "bookings.phase.WAITING_DROPOFF": "Waiting for dropoff",
  "bookings.phase.REVIEW_REQUIRED": "Review required",
  "bookings.phase.COMPLETED": "Completed",
  "bookings.phase.REJECTED": "Rejected",
  "bookings.phase.CANCELLED": "Cancelled",
  "bookings.phase.EXPIRED": "Expired",
  "bookings.action.BOOKING_CREATED": "Booking requested",
  "bookings.action.BOOKING_APPROVED": "Booking accepted",
  "bookings.action.BOOKING_REJECTED": "Booking rejected",
  "bookings.action.BOOKING_PICKED_UP": "Pickup confirmed",
  "bookings.action.BOOKING_DROPPED_OFF": "Dropoff confirmed",
  "bookings.action.BOOKING_CANCELLED": "Booking cancelled",
  "bookings.action.BOOKING_EXPIRED": "Booking expired",
  "bookings.action.RIDE_STARTED": "Ride started",
  "bookings.action.RIDE_COMPLETED": "Ride completed",
  "bookings.action.RIDE_CANCELLED": "Ride cancelled",
  "bookings.action.RIDE_EXPIRED": "Ride expired",
  "bookings.role.DRIVER": "Driver",
  "bookings.role.RIDER": "Rider",
  "bookings.role.GUARDIAN": "Guardian",
  "bookings.role.ADMIN": "Admin",
  "bookings.role.SYSTEM": "System",
  "bookings.role.UNKNOWN": "Person not recorded",
  "bookings.source.LIFECYCLE_API": "Ride confirmation",
  "bookings.source.REQUEST_JOIN": "Joined via request",
  "bookings.source.RIDE_OFFER": "Driver offer",
  "bookings.source.ACTION_ITEM": "Action item",
  "bookings.source.CHILD_TRANSPORT": "Linked child transport",
  "bookings.source.ADMIN": "Admin dashboard",
  "bookings.source.RECURRING_SERIES": "Recurring series",
  "bookings.source.SYSTEM_JOB": "Automatic system job",
  "bookings.source.HISTORICAL": "Existing timestamp",
  "bookings.reason.NONE": "",
  "bookings.reason.PICKUP_IMPLIED_START": "Automatically started by pickup confirmation",
  "bookings.reason.ALL_PASSENGERS_DROPPED_OFF": "All passengers dropped off",
  "bookings.reason.ADMIN_COMPLETION": "Admin completed the ride",
  "bookings.reason.NO_START_EXPIRY": "Ride was not started",
  "bookings.reason.CAPACITY_REJECTION": "No remaining capacity",
  "bookings.reason.REQUEST_WITHDRAWN": "Request withdrawn",
  "bookings.reason.SERIES_CHANGE": "Recurring series changed",
  "bookings.reason.CONFIRMATION_EXPIRED": "Series confirmation expired",
  "bookings.reason.TRANSPORT_SKIPPED": "Transport skipped",
  "bookings.reason.ACCOUNT_DELETED": "Account deleted",

  'app.brand': 'mobie',
  'nav.profile': 'Profile',
  'nav.dashboard': 'Analytics',
  'nav.communities': 'Communities',
  'nav.interests': 'Interests',
  'nav.rides': 'Rides',
  'nav.events': 'Events',
  'nav.locations': 'Locations',
  'nav.payouts': 'Payouts',
  'nav.paymentOps': 'Payment Ops',
  'nav.users': 'Users',
  'nav.leads': 'Leads',
  'nav.notifications': 'Campaigns',
  'nav.aiAssistant': 'AI Assistant',
  'action.login': 'Login',
  'action.logout': 'Logout',
  'action.edit': 'Edit',
  'action.delete': 'Delete',
  'action.back': 'Back',
  'action.save': 'Save',
  'action.cancel': 'Cancel',
  'action.refresh': 'Refresh',
  'action.apply': 'Apply',
  'action.clear': 'Clear',
  'action.previous': 'Previous',
  'action.next': 'Next',
  'action.expand': 'Expand',
  'action.collapse': 'Collapse',

  'common.loading': 'Loading…',
  'common.error': 'Error',
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.saving': 'Saving…',
  'common.deleting': 'Deleting…',
  'common.created': 'Created',
  'common.pageOf': 'Page {page} of {total}',
  'common.records': '{count} records',
  'common.noResults': 'No results match the current filters.',

  'dashboard.title': 'Community dashboard',
  'dashboard.last7': 'Last 7 days',
  'dashboard.last30': 'Last 30 days',
  'dashboard.activity': 'Activity (last {days} days)',
  'dashboard.legend': 'Bars: rides · Line: CO₂',
  'dashboard.totalCo2': 'Total CO₂ saved',
  'dashboard.totalRides': 'Total rides',
  'dashboard.tripsCompleted': 'Trips completed',
  'dashboard.moneyPerDay': 'Money saved (per day)',
  'dashboard.totalMoney': 'Total',
  'dashboard.avgOccupancy': 'Average occupancy',
  'dashboard.ridesPerUser': 'Rides per user',
  'analytics.title': 'Analytics',
  'analytics.description': 'Understand user growth, activation, marketplace health, and community performance.',
  'analytics.period': 'Analytics period',
  'analytics.lastDays': 'Last {days} days',
  'analytics.allTime': 'All Time',
  'analytics.categories': 'Analytics categories',
  'analytics.tab.overview': 'Overview',
  'analytics.tab.groups': 'Groups',
  'analytics.goals.title': 'Our goals',
  'analytics.goals.scope': 'Current users and series, cumulative shared rides, and active groups in the last 7 days. Independent of the selected activity period.',
  'analytics.goals.users': 'Registered users',
  'analytics.goals.usersHint': 'All registered accounts except deleted users; onboarding completion is not required.',
  'analytics.goals.series': 'Active recurring ride series',
  'analytics.goals.seriesHint': '{groups} in groups · {public} outside groups',
  'analytics.goals.rides': 'Completed rides outside groups',
  'analytics.goals.groups': 'Active groups',
  'analytics.goals.ridesHint': 'Each completed ride counts once, with at least one completed passenger trip from another household.',
  'analytics.rides.offered': 'Offered rides',
  'analytics.rides.completed': 'Completed rides with passengers',
  'analytics.rides.passengers': 'Completed passenger trips',
  'analytics.marketplace.publicRides': 'Rides outside groups',
  'analytics.marketplace.scope': 'Rides, requests and bookings outside groups. Search statistics cover the whole app.',
  'analytics.groups.total': 'Total groups',
  'analytics.groups.scope': 'Group rides only. Ride volume follows the selected period; the active-group threshold always uses the last 7 days.',
  'analytics.groups.activeHint': 'At least 5 completed rides with passengers from another household in the last 7 days. Rides with only the driver’s own children do not count.',
  'analytics.groups.activity': 'Group ride volume',
  'analytics.groups.activityHint': 'Offered rides by creation date; completed rides and passenger trips by completion date. A ride with three passengers counts as one ride and three passenger trips.',
  'analytics.groups.details': 'Group activity',
  'analytics.groups.group': 'Group',
  'analytics.groups.status': 'Status',
  'analytics.groups.last7Rides': 'Rides · last 7 days',
  'analytics.groups.last7Passengers': 'Passenger trips · last 7 days',
  'analytics.groups.active': 'Active',
  'analytics.groups.belowThreshold': 'Below threshold',
  'analytics.tab.users': 'Users',
  'analytics.tab.onboarding': 'Onboarding',
  'analytics.tab.interests': 'Interests',
  'analytics.interests.snapshot': 'Current interest selections',
  'analytics.interests.scope': 'All users except deleted accounts. This is the current profile state, independent of the activity period. Users can choose multiple interests.',
  'analytics.interests.updated': 'Updated: {date}',
  'analytics.interests.manage': 'Manage interests',
  'analytics.interests.error': 'Could not load interest analytics.',
  'analytics.interests.retry': 'Retry',
  'analytics.interests.with': 'Users with interests',
  'analytics.interests.without': 'Users without interests',
  'analytics.interests.coverage': '{rate} of {total} users',
  'analytics.interests.withoutHint': 'No interests selected in their profile',
  'analytics.interests.averageAll': 'Average per user',
  'analytics.interests.averageAllHint': 'All users, including those without interests',
  'analytics.interests.averageParticipants': 'Average when provided',
  'analytics.interests.averageParticipantsHint': 'Only users with at least one interest',
  'analytics.interests.participation': 'Interest participation',
  'analytics.interests.totalSelections': '{count} interest selections in total',
  'analytics.interests.noUsers': 'No users yet. Averages and percentages will appear once users are available.',
  'analytics.interests.participatingCount': '{count} users with interests',
  'analytics.interests.missingCount': '{count} users without interests',
  'analytics.interests.popularity': 'Most popular interests',
  'analytics.interests.popularityHint': 'Users per interest, sorted by popularity. Percentages refer to all users; multiple selections are possible.',
  'analytics.interests.categories': 'Interest categories',
  'analytics.interests.categoriesHint': 'Users with at least one interest in the category. Each user counts once per category; percentages refer to all users.',
  'analytics.interests.distribution': 'How many interests do users select?',
  'analytics.interests.distributionHint': 'Users grouped by their number of selected interests. Percentages refer to all users.',
  'analytics.interests.oneSelection': '1 interest',
  'analytics.interests.selectionCount': '{count} interests',
  'analytics.tab.marketplace': 'Marketplace',
  'analytics.tab.communities': 'Communities',
  'analytics.tab.guidedTour': 'App tour',
  'analytics.loading': 'Loading analytics…',
  'analytics.error': 'Analytics could not be loaded',
  'analytics.noData': 'No data is available for this period.',
  'analytics.metric.totalUsers': 'Total users',
  'analytics.metric.newUsers': 'New users',
  'analytics.metric.activeUsers': 'Active users',
  'analytics.metric.completedTrips': 'Completed passenger trips',
  'analytics.metric.onboardingRate': 'Onboarding completion',
  'analytics.metric.phoneCoverage': 'Phone number provided',
  'analytics.phoneCoverage.count': '{count} of {total} new users',
  'analytics.metric.verificationRate': 'Verification rate',
  'analytics.verification.count': '{count} of {total} new users verified',
  'analytics.metric.rideSearches': 'Ride searches',
  'analytics.activity.title': 'Growth and activity',
  'analytics.activity.description': 'Daily unique activity in the selected period.',
  "analytics.push.title": "Push reach",
  "analytics.push.description": "Push registration among users active in the selected period. All Time includes all users. Each user counts once. A saved token does not confirm delivery or notification permission.",
  "analytics.push.allUsers": "All users",
  "analytics.push.activeDays": "Active in the last {days} days",
  "analytics.push.audience": "Audience",
  "analytics.push.users": "Users",
  "analytics.push.registered": "With push registration",
  "analytics.push.missing": "Without registration",
  "analytics.push.coverage": "Coverage",
  "analytics.push.unknown": "Unknown platform",
  "analytics.push.platformHint": "Platform columns count users with an active push installation. Users with both iOS and Android appear in both columns. Older profile tokens without an active installation are included under unknown platform. Category preferences are not included.",
  "analytics.push.history": "Recorded history",
  "analytics.push.historyMetric": "History metric",
  "analytics.push.rate": "Coverage (%)",
  "analytics.push.count": "Registered users",
  "analytics.push.chart": "Push registration history",
  "analytics.push.trackingSince": "Tracking since {date} · Europe/Vienna. The latest recorded value per day is retained; today is updated hourly. Missing days are not reconstructed.",
  "analytics.push.pending": "The first sample is recorded automatically after startup.",
  "analytics.push.noHistory": "No recorded samples in the selected period.",
  "analytics.push.historyDetails": "Show recorded counts and timestamps",
  "analytics.push.recordedAt": "Recorded at (Europe/Vienna)",
  "analytics.push.updated": "Current registration as of {date} · Europe/Vienna",
  "analytics.push.refresh": "Refresh",
  "analytics.push.error": "Could not load push reach. Please try refreshing.",
  'analytics.platforms.title': 'App operating systems',
  'analytics.platforms.description': 'Installations with an app session in the selected period.',
  'analytics.platforms.knownInstallations': '{count} known installations',
  'analytics.platforms.distribution': 'Operating system distribution: iOS {ios}, Android {android}',
  'analytics.dataSince': 'Session and search analytics are available since {date}.',
  'analytics.users.funnel': 'New-user activation funnel',
  'analytics.users.funnelDescription': 'Current progress of users who registered during the selected period.',
  'analytics.users.acquisition': 'Acquisition source',
  'analytics.users.mobilityRole': 'Mobility role',
  'analytics.users.language': 'Language',
  'analytics.onboarding.open': 'Open onboardings',
  'analytics.onboarding.completion': 'Completion rate',
  'analytics.onboarding.biggest': 'Biggest drop-off',
  'analytics.onboarding.steps': 'Open users by onboarding step',
  'analytics.onboarding.description': 'First incomplete step for users registered during the selected period who have not completed onboarding yet.',
  'analytics.marketplace.zeroResults': 'Zero-result rate',
  'analytics.marketplace.requests': 'Ride requests',
  'analytics.marketplace.rides': 'Rides offered',
  'analytics.marketplace.activity': 'Marketplace activity',
  'analytics.marketplace.activityDescription': 'Rides outside groups: offers by creation date, shared rides and passenger trips by completion date.',
  'analytics.marketplace.searchQuality': 'Search and booking quality',
  'analytics.marketplace.successfulSearches': 'Searches with results',
  'analytics.marketplace.zeroResultSearches': 'Searches without results',
  'analytics.marketplace.avgResults': 'Average results per search',
  'analytics.marketplace.bookings': 'Bookings created',
  'analytics.marketplace.cancelledBookings': 'Bookings cancelled',
  'analytics.communities.total': 'Communities',
  'analytics.communities.memberships': 'Current memberships',
  'analytics.communities.netGrowth': 'Net membership growth',
  'analytics.communities.byUsers': 'Communities by users',
  'analytics.communities.byUsersDescription': 'Current active memberships in the largest communities.',
  'analytics.communities.growth': 'Recent growth',
  'analytics.communities.growthDescription': 'Net member change during the selected period.',
  'analytics.communities.details': 'Community growth details',
  'analytics.managerVisits.title': 'Manager dashboard visits',
  'analytics.managerVisits.description': 'One visit per dashboard opening. Filter changes and retries count once. Times: {timeZone}.',
  'analytics.managerVisits.visits': 'Dashboard visits',
  'analytics.managerVisits.managers': 'Managers who visited',
  'analytics.managerVisits.byDay': 'Visits by day, manager and community',
  'analytics.managerVisits.day': 'Day',
  'analytics.managerVisits.manager': 'Manager',
  'analytics.managerVisits.first': 'First visit',
  'analytics.managerVisits.last': 'Last visit',
  'analytics.managerVisits.recent': 'Latest 100 visits in the selected period',
  'analytics.managerVisits.when': 'Date and time',
  'analytics.managerVisits.empty': 'No recorded visits in the selected period.',
  'analytics.communities.community': 'Community',
  'analytics.communities.current': 'Current users',
  'analytics.communities.period': 'Start → end',
  'analytics.communities.joined': 'Joined',
  'analytics.communities.left': 'Left',
  'analytics.communities.net': 'Net',
  'analytics.communities.rate': 'Growth',
  'analytics.guidedTour.totalRuns': 'Tour starts',
  'analytics.guidedTour.uniqueUsers': 'Unique users',
  'analytics.guidedTour.completionRate': 'Completion rate',
  'analytics.guidedTour.skipped': 'Skipped',
  'analytics.guidedTour.abandoned': 'Abandoned',
  'analytics.guidedTour.inProgress': 'In progress',
  'analytics.guidedTour.outcomes': 'Tour outcomes',
  'analytics.guidedTour.outcomesDescription': 'Completed, skipped, abandoned, and currently active runs.',
  'analytics.guidedTour.dropOffs': 'Drop-off steps',
  'analytics.guidedTour.dropOffsDescription': 'Last reached step for skipped or abandoned tours.',
  'analytics.guidedTour.branches': 'Tour modes',
  'analytics.guidedTour.branchesDescription': 'Tour starts split by driver and rider mode.',
  'analytics.value.REGISTERED': 'Registered',
  'analytics.value.ONBOARDING_COMPLETED': 'Onboarding completed',
  'analytics.value.SEARCHED': 'Searched for a ride',
  'analytics.value.CREATED_RIDE_OR_REQUEST': 'Created ride or request',
  'analytics.value.COMPLETED_TRIP': 'Completed a trip',
  'analytics.value.NAME': 'Name & birthday',
  'analytics.value.PROFILE': 'Profile',
  'analytics.value.TERMS': 'Terms',
  'analytics.value.SOURCE': 'Acquisition source',
  'analytics.value.EVENT': 'Event',
  'analytics.value.COMMUNITY': 'Community',
  'analytics.value.INTERESTS': 'Interests',
  'analytics.value.USAGE': 'Mobility role',
  'analytics.value.RIDE_PLAN': 'Ride plan',
  'analytics.value.VEHICLE': 'Vehicle',
  'analytics.value.VERIFICATION_CHOICE': 'Verification choice',
  'analytics.value.VERIFICATION': 'Identity verification',
  'analytics.value.STRIPE': 'Stripe onboarding',
  'analytics.value.FINISH': 'Final confirmation',
  'analytics.value.OTHER': 'Other',
  'analytics.value.UNKNOWN': 'Not provided',
  'analytics.value.DRIVER': 'Driver',
  'analytics.value.RIDER': 'Rider',
  'analytics.value.BOTH': 'Driver and rider',
  'analytics.value.COMPLETED': 'Completed',
  'analytics.value.SKIPPED': 'Skipped',
  'analytics.value.ABANDONED': 'Abandoned',
  'analytics.value.IN_PROGRESS': 'In progress',
  'analytics.value.mode': 'Mode selection',
  'analytics.value.nearby': 'Nearby rides',
  'analytics.value.locations': 'Saved places',
  'analytics.value.recurring': 'Recurring rides',
  'analytics.value.singleRide': 'Create a ride',
  'analytics.value.search': 'Find a ride',
  'analytics.value.activity': 'Activity',
  'analytics.value.social': 'Social',
  'analytics.value.events': 'Events',
  'rides.title': 'Rides',
  'rides.description': 'Admin operations view for all rides, bookings, riders, payments, and lifecycle actions.',
  'rides.ongoing.loading': 'Ongoing rides loading…',
  'rides.ongoing.count': '{count} ongoing rides now',
  'rides.ongoing.count.one': '1 ongoing ride now',
  'rides.filtersCleared': 'Ride filters cleared.',
  'rides.loading': 'Loading rides…',
  'rides.found': '{count} rides found',
  'rides.searchPlaceholder': 'Search ride ID, booking ID, user ID, name, or email',
  'rides.filterByUser': 'Filter by user',
  'rides.allStatuses': 'All statuses',
  'rides.sort.label': 'Sort rides',
  'rides.sort.time': 'Departure date',
  'rides.sort.createdAt': 'Created date',
  'rides.createdAt': 'Created {date}',
  'rides.type.all': 'All types',
  'rides.type.single': 'Single ride',
  'rides.type.recurring': 'Recurring ride',
  'rides.noMatches': 'No rides match the current filters.',
  'rides.status.scheduled': 'Scheduled',
  'rides.status.started': 'Started',
  'rides.status.completed': 'Completed',
  'rides.status.cancelled': 'Cancelled',
  'rides.status.expired': 'Expired',
  'rides.table.departureRoute': 'Departure & route',
  'rides.table.actions': 'Actions',
  'rides.table.ride': 'Ride',
  'rides.table.created': 'Created',
  'rides.table.type': 'Type',
  'rides.table.route': 'Route',
  'rides.table.driver': 'Driver',
  'rides.table.status': 'Status',
  'rides.table.bookings': 'Bookings',
  'rides.table.payments': 'Payments',
  'rides.bookingsCount': '{count} bookings',
  'rides.seatsApproved': '{approved}/{total} seats approved',
  'rides.paymentIssues': '{count} payment issue(s)',
  'rides.noPaymentIssues': 'No payment issues',
  'rides.details.loading': 'Loading ride details…',
  'rides.details.id': 'Ride ID',
  'rides.details.copyId': 'Copy ride ID',
  'rides.action.expandInline': 'Show quick details',
  'rides.action.collapseInline': 'Hide quick details',
  'rides.action.start': 'Start',
  'rides.action.cancel': 'Cancel',
  'rides.action.complete': 'Complete',
  'rides.action.openDetails': 'Details & chat',
  'rides.action.working': 'Working…',
  'ride.date': 'Date',
  'ride.time': 'Time',
  'ride.seats': 'Seats',
  'rideRequest.title': 'Ride Requests',
  'rideRequest.create': 'Create ride request',
  'rideRequest.departure': 'Departure',
  'rideRequest.destination': 'Destination',
  'rideRequest.status': 'Status',
  'rideRequest.status.requested': 'Requested',
  'rideRequest.status.approved': 'Approved',
  'rideRequest.status.cancelled': 'Cancelled',
  'rideRequest.status.finished': 'Finished',
  'rideRequest.save': 'Save ride request',
  'locations.title': 'My Locations',
  'locations.editTitle': 'Edit Location',
  'locations.createTitle': 'Create New Location',
  'locations.createNew': 'New Location',
  'locations.create': 'Create Location',
  'locations.createSuccess': 'Location created successfully',
  'locations.searchLocation': 'Search for location',
  'locations.searchOrClickMap': 'Search for a location or click on the map',
  'locations.locationDetails': 'Location Details',
  'locations.noLocations': 'You haven\'t saved any locations yet.',
  'locations.searchPlaceholder': 'Search place or address…',
  'locations.select': 'Select',
  'locations.selected': 'Selected location',
  'locations.save': 'Save location',
  'locations.confirmDelete': 'Are you sure you want to delete this location?',
  'locations.deleteSuccess': 'Location deleted successfully',
  'locations.updateSuccess': 'Location updated successfully',
  'locations.isPrivate': 'Privacy',
  'locations.private': 'Private',
  'locations.public': 'Public',
  'locations.publicName': 'Public name',
  'locations.publicNamePlaceholder': 'e.g. Main Street, Garching',
  'locations.publicNameDescription': 'This name is visible to other users when you share rides.',
  'locations.privateName': 'Private name (optional)',
  'locations.privateNamePlaceholder': 'e.g. Home, Work, Gym',
  'locations.privateNameDescription': 'This name is only visible to you.',
  'locations.privateDescription': 'Private locations show your custom name only to you.',
  'locations.coordinates': 'Coordinates',
  'locations.longitude': 'Longitude',
  'locations.latitude': 'Latitude',
  'locations.addToFavorites': 'Add to favorites',
  'locations.removeFromFavorites': 'Remove from favorites',
  'locations.favorites': 'Favorites',
  'directions.title': 'Directions',
  'directions.from': 'From',
  'directions.to': 'To',
  'directions.clear': 'Clear',
  'directions.fetch': 'Fetch route',
  'directions.distance': 'Distance',
  'directions.duration': 'Duration',
  'directions.alternatives': 'Alternatives',
  'ride.save': 'Save ride',
  'paymentOps.title': 'Payment Ops',
  'paymentOps.description': 'Triage booking payment capture failures, Stripe Connect routing gaps, stale payment intents, and booking state mismatches without database access.',
  'paymentOps.searchPlaceholder': 'Search booking, ride, payment intent, user',
  'paymentOps.allIssues': 'All',
  'paymentOps.anyStatus': 'Any status',
  'paymentOps.loading': 'Loading…',
  'paymentOps.bookings': '{count} bookings',
  'paymentOps.metric.allBookings': 'All bookings',
  'paymentOps.metric.authorized': 'Authorized',
  'paymentOps.metric.pending': 'Pending',
  'paymentOps.metric.captured': 'Captured',
  'paymentOps.metric.cancelled': 'Cancelled',
  'paymentOps.metric.notRequired': 'Not required',
  'paymentOps.metric.captureFailed': 'Capture failed',
  'paymentOps.metric.notCaptured': 'Not captured',
  'paymentOps.metric.noConnectRouting': 'No Connect routing',
  'paymentOps.metric.connectIncomplete': 'Connect incomplete',
  'paymentOps.metric.connectStale': 'Connect stale',
  'paymentOps.metric.openIntents': 'Open intents',
  'paymentOps.metric.stateMismatch': 'State mismatch',
  'paymentOps.metric.missingIntent': 'Missing intent',
  'paymentOps.noMatches': 'No bookings match the current filters.',
  'paymentOps.retryCapture': 'Retry capture',
  'paymentOps.retrying': 'Retrying…',
  'paymentOps.cancelAuth': 'Cancel auth',
  'paymentOps.confirmRetry': 'Retry capture for this booking?',
  'paymentOps.confirmCancelAuth': 'Cancel the Stripe authorization for this inactive booking? This does not refund captured money.',
  'paymentOps.retryFinished': 'Capture retry finished.',
  'paymentOps.retryFailed': 'Capture retry failed',
  'paymentOps.authCancelled': 'Authorization cancelled.',
  'paymentOps.authCancelFailed': 'Authorization cancellation failed',
  'paymentOps.issue.ALL': 'All',
  'paymentOps.issue.CAPTURE_FAILED': 'Capture failed',
  'paymentOps.issue.COMPLETED_NOT_CAPTURED': 'Completed, not captured',
  'paymentOps.issue.CAPTURED_WITHOUT_CONNECT_ROUTING': 'Captured without Connect routing',
  'paymentOps.issue.DRIVER_CONNECT_INCOMPLETE': 'Driver Connect incomplete',
  'paymentOps.issue.DRIVER_CONNECT_IN_REVIEW': 'Driver Connect in review',
  'paymentOps.issue.CONNECT_STATUS_STALE': 'Connect status stale',
  'paymentOps.issue.INACTIVE_WITH_OPEN_INTENT': 'Inactive with open intent',
  'paymentOps.issue.STATE_MISMATCH': 'State mismatch',
  'paymentOps.issue.PAYMENT_INTENT_MISSING': 'Missing intent',
  'assistant.prompt.findRide': 'Find me a ride from Vienna Central Station to Graz tomorrow morning.',
  'assistant.prompt.showBookings': 'Show my active bookings.',
  'assistant.prompt.bookRide': 'I want to book a ride from Linz to Salzburg on Friday at 17:00.',
  'assistant.error.initialize': 'Failed to initialize assistant',
  'assistant.error.send': 'Failed to send message',
  'assistant.brand': 'Mobie AI',
  'assistant.title': 'Ride booking assistant',
  'assistant.description': 'Search rides, check estimates, list bookings, and prepare bookings or cancellations in chat. The backend keeps all ride facts tool-backed and confirmation-gated.',
  'assistant.session': 'Session',
  'assistant.session.active': 'Active: {id}…',
  'assistant.session.creating': 'Creating chat session…',
  'assistant.session.description': 'This is the text-first agent flow. The same backend session model can later power voice.',
  'assistant.suggestedPrompts': 'Suggested prompts',
  'assistant.confirmationHelp': 'Confirmation help',
  'assistant.confirmationHint': 'When the assistant prepares a booking or cancellation, reply with a clear yes or no.',
  'assistant.channel': 'Chat channel',
  'assistant.chatTitle': 'AI booking chat',
  'assistant.status.initializing': 'Initializing',
  'assistant.status.thinking': 'Thinking',
  'assistant.status.ready': 'Ready',
  'assistant.chatDescription': 'The admin app now talks to the backend agent session API. The backend in turn calls the LLM and only exposes a fixed set of ride tools to it.',
  'assistant.initializingSession': 'Creating a new assistant session…',
  'assistant.empty.eyebrow': 'Blank conversation',
  'assistant.empty.title': 'Start with a ride question',
  'assistant.empty.description': 'Ask for a ride, a pricing estimate, or your current bookings. When the assistant prepares a mutation, just answer yes or no.',
  'assistant.message.you': 'You',
  'assistant.typing': 'Mobie AI is checking the ride tools…',
  'assistant.inputPlaceholder': 'Ask to search a ride, estimate a pickup, book an option, or show your active bookings…',
  'assistant.footerHint': 'The assistant can prepare bookings and cancellations, but it still needs a final yes/no confirmation from you.',
  'assistant.action.sending': 'Sending…',
  'assistant.action.send': 'Send message',
};

const de: Record<string, string> = {
  "bookings.tabs": "Fahrten und Buchungen",
  "bookings.tab": "Buchungen",
  "bookings.title": "Buchungen",
  "bookings.total": "Buchungen gesamt",
  "bookings.description": "Annahme, Fahrtstart, Abholung und Absetzen aller Buchungen im Blick.",
  "bookings.detailTitle": "Buchungsverlauf",
  "bookings.timeline": "Zeitverlauf",
  "bookings.noEvents": "Noch keine Schritte erfasst.",
  "bookings.unknownActor": "Person nicht erfasst",
  "bookings.historical": "Historischer Zeitpunkt",
  "bookings.offerCreated": "Buchung aus Fahrerangebot erstellt",
  "bookings.view": "Ansicht",
  "bookings.view.today": "Heute",
  "bookings.view.open": "Alle offenen",
  "bookings.view.all": "Alle",
  "bookings.view.range": "Zeitraum",
  "bookings.search": "Suche",
  "bookings.searchPlaceholder": "Buchungs-/Fahrt-ID, Name oder E-Mail",
  "bookings.phaseLabel": "Aktueller Schritt",
  "bookings.anyPhase": "Alle Schritte",
  "bookings.pageSize": "Zeilen",
  "bookings.from": "Von",
  "bookings.to": "Bis",
  "bookings.invalidRange": "Der Beginn muss vor oder am Ende liegen.",
  "bookings.booking": "Buchung / Fahrt",
  "bookings.requester": "Anfragende Person",
  "bookings.driver": "Fahrer",
  "bookings.route": "Abholung → Absetzen",
  "bookings.scheduled": "Geplante Fahrt",
  "bookings.lastActivity": "Letzte Aktivität",
  "bookings.lastSuccess": "Zuletzt erfolgreich aktualisiert:",
  "bookings.loadFailed": "Buchungen konnten nicht geladen werden.",
  "bookings.retry": "Erneut laden",
  "bookings.ride": "Fahrt öffnen",
  "bookings.lifecycle": "Buchungsverlauf",
  "bookings.openTimeline": "Verlauf öffnen",
  "bookings.closeTimeline": "Verlauf schließen",
  "bookings.exactFilter": "Exakte Buchung:",
  "bookings.effect.DERIVED": "Automatisch abgeleitet",
  "bookings.effect.BACKFILLED": "durch Admin nachgetragen",
  "bookings.effect.DIRECT": "Direkt bestätigt",
  "bookings.phase.WAITING_ACCEPTANCE": "Wartet auf Annahme",
  "bookings.phase.WAITING_START": "Wartet auf Fahrtstart",
  "bookings.phase.WAITING_PICKUP": "Wartet auf Abholung",
  "bookings.phase.WAITING_DROPOFF": "Wartet auf Absetzen",
  "bookings.phase.REVIEW_REQUIRED": "Klärung nötig",
  "bookings.phase.COMPLETED": "Abgeschlossen",
  "bookings.phase.REJECTED": "Abgelehnt",
  "bookings.phase.CANCELLED": "Storniert",
  "bookings.phase.EXPIRED": "Abgelaufen",
  "bookings.action.BOOKING_CREATED": "Buchung angefragt",
  "bookings.action.BOOKING_APPROVED": "Buchung angenommen",
  "bookings.action.BOOKING_REJECTED": "Buchung abgelehnt",
  "bookings.action.BOOKING_PICKED_UP": "Abholung bestätigt",
  "bookings.action.BOOKING_DROPPED_OFF": "Absetzen bestätigt",
  "bookings.action.BOOKING_CANCELLED": "Buchung storniert",
  "bookings.action.BOOKING_EXPIRED": "Buchung abgelaufen",
  "bookings.action.RIDE_STARTED": "Fahrt gestartet",
  "bookings.action.RIDE_COMPLETED": "Fahrt abgeschlossen",
  "bookings.action.RIDE_CANCELLED": "Fahrt storniert",
  "bookings.action.RIDE_EXPIRED": "Fahrt abgelaufen",
  "bookings.role.DRIVER": "Fahrer",
  "bookings.role.RIDER": "Mitfahrer",
  "bookings.role.GUARDIAN": "Sorgeberechtigter",
  "bookings.role.ADMIN": "Admin",
  "bookings.role.SYSTEM": "System",
  "bookings.role.UNKNOWN": "Person nicht erfasst",
  "bookings.source.LIFECYCLE_API": "Fahrtbestätigung",
  "bookings.source.REQUEST_JOIN": "Beitritt über Gesuch",
  "bookings.source.RIDE_OFFER": "Fahrerangebot",
  "bookings.source.ACTION_ITEM": "Action Item",
  "bookings.source.CHILD_TRANSPORT": "Verknüpfter Kindertransport",
  "bookings.source.ADMIN": "Admin-Dashboard",
  "bookings.source.RECURRING_SERIES": "Fahrtserie",
  "bookings.source.SYSTEM_JOB": "Automatischer Systemjob",
  "bookings.source.HISTORICAL": "Vorhandener Zeitpunkt",
  "bookings.reason.NONE": "",
  "bookings.reason.PICKUP_IMPLIED_START": "Automatisch durch Abholbestätigung",
  "bookings.reason.ALL_PASSENGERS_DROPPED_OFF": "Alle Mitfahrer abgesetzt",
  "bookings.reason.ADMIN_COMPLETION": "Fahrtabschluss durch Admin",
  "bookings.reason.NO_START_EXPIRY": "Fahrt wurde nicht gestartet",
  "bookings.reason.CAPACITY_REJECTION": "Keine freien Plätze",
  "bookings.reason.REQUEST_WITHDRAWN": "Gesuch zurückgezogen",
  "bookings.reason.SERIES_CHANGE": "Fahrtserie geändert",
  "bookings.reason.CONFIRMATION_EXPIRED": "Serienbestätigung abgelaufen",
  "bookings.reason.TRANSPORT_SKIPPED": "Transport übersprungen",
  "bookings.reason.ACCOUNT_DELETED": "Konto gelöscht",

  'app.brand': 'mobie',
  'nav.profile': 'Profil',
  'nav.dashboard': 'Analytics',
  'nav.communities': 'Communities',
  'nav.interests': 'Interessen',
  'nav.rides': 'Fahrten',
  'nav.events': 'Events',
  'nav.locations': 'Orte',
  'nav.payouts': 'Auszahlungen',
  'nav.paymentOps': 'Zahlungen',
  'nav.users': 'Nutzer',
  'nav.leads': 'Leads',
  'nav.notifications': 'Kampagnen',
  'nav.aiAssistant': 'KI-Assistent',
  'action.login': 'Anmelden',
  'action.logout': 'Abmelden',
  'action.edit': 'Bearbeiten',
  'action.delete': 'Löschen',
  'action.back': 'Zurück',
  'action.save': 'Speichern',
  'action.cancel': 'Abbrechen',
  'action.refresh': 'Aktualisieren',
  'action.apply': 'Anwenden',
  'action.clear': 'Zurücksetzen',
  'action.previous': 'Zurück',
  'action.next': 'Weiter',
  'action.expand': 'Öffnen',
  'action.collapse': 'Schließen',

  'common.loading': 'Lädt…',
  'common.error': 'Fehler',
  'common.save': 'Speichern',
  'common.cancel': 'Abbrechen',
  'common.saving': 'Speichern…',
  'common.deleting': 'Löschen…',
  'common.created': 'Erstellt',
  'common.pageOf': 'Seite {page} von {total}',
  'common.records': '{count} Einträge',
  'common.noResults': 'Keine Ergebnisse für die aktuellen Filter.',

  'dashboard.title': 'Community-Dashboard',
  'dashboard.last7': 'Letzte 7 Tage',
  'dashboard.last30': 'Letzte 30 Tage',
  'dashboard.activity': 'Aktivität (letzte {days} Tage)',
  'dashboard.legend': 'Balken: Fahrten · Linie: CO₂',
  'dashboard.totalCo2': 'Gespartes CO₂ gesamt',
  'dashboard.totalRides': 'Fahrten gesamt',
  'dashboard.tripsCompleted': 'Abgeschlossene Fahrten',
  'dashboard.moneyPerDay': 'Ersparnis (pro Tag)',
  'dashboard.totalMoney': 'Summe',
  'dashboard.avgOccupancy': 'Durchschn. Auslastung',
  'dashboard.ridesPerUser': 'Fahrten pro Nutzer',
  'analytics.title': 'Analytics',
  'analytics.description': 'Nutzerwachstum, Aktivierung, Marktplatz und Community-Entwicklung im Überblick.',
  'analytics.period': 'Analysezeitraum',
  'analytics.lastDays': 'Letzte {days} Tage',
  'analytics.allTime': 'All Time',
  'analytics.categories': 'Analytics-Kategorien',
  'analytics.tab.overview': 'Übersicht',
  'analytics.tab.groups': 'Gruppen',
  'analytics.goals.title': 'Unsere Ziele',
  'analytics.goals.scope': 'Aktueller Nutzer- und Serienbestand, kumulierte gemeinsame Fahrten und aktive Gruppen der letzten 7 Tage. Unabhängig vom gewählten Analysezeitraum.',
  'analytics.goals.users': 'Registrierte Nutzer',
  'analytics.goals.usersHint': 'Alle registrierten Accounts außer gelöschten Nutzern; abgeschlossenes Onboarding ist nicht erforderlich.',
  'analytics.goals.series': 'Aktive wiederkehrende Fahrtserien',
  'analytics.goals.seriesHint': '{groups} in Gruppen · {public} außerhalb von Gruppen',
  'analytics.goals.rides': 'Abgeschlossene Fahrten außerhalb von Gruppen',
  'analytics.goals.groups': 'Aktive Gruppen',
  'analytics.goals.ridesHint': 'Jede abgeschlossene Fahrt zählt einmal, mit mindestens einer abgeschlossenen Mitfahrt aus einem anderen Haushalt.',
  'analytics.rides.offered': 'Angebotene Fahrten',
  'analytics.rides.completed': 'Abgeschlossene Fahrten mit Passengers',
  'analytics.rides.passengers': 'Abgeschlossene Mitfahrten',
  'analytics.marketplace.publicRides': 'Fahrten außerhalb von Gruppen',
  'analytics.marketplace.scope': 'Fahrten, Anfragen und Buchungen außerhalb von Gruppen. Suchstatistiken beziehen sich auf die gesamte App.',
  'analytics.groups.total': 'Gruppen gesamt',
  'analytics.groups.scope': 'Nur Gruppenfahrten. Das Fahrtvolumen folgt dem gewählten Zeitraum; die Aktivitätsschwelle verwendet immer die letzten 7 Tage.',
  'analytics.groups.activeHint': 'Mindestens 5 abgeschlossene Fahrten mit Passengers aus einem anderen Haushalt in den letzten 7 Tagen. Fahrten nur mit eigenen Kindern zählen nicht.',
  'analytics.groups.activity': 'Fahrtvolumen in Gruppen',
  'analytics.groups.activityHint': 'Angebotene Fahrten nach Erstelldatum; abgeschlossene Fahrten und Mitfahrten nach Abschlussdatum. Eine Fahrt mit drei Passengers zählt als eine Fahrt und drei Mitfahrten.',
  'analytics.groups.details': 'Gruppenaktivität',
  'analytics.groups.group': 'Gruppe',
  'analytics.groups.status': 'Status',
  'analytics.groups.last7Rides': 'Fahrten · letzte 7 Tage',
  'analytics.groups.last7Passengers': 'Mitfahrten · letzte 7 Tage',
  'analytics.groups.active': 'Aktiv',
  'analytics.groups.belowThreshold': 'Unter Schwelle',
  'analytics.tab.users': 'Nutzer',
  'analytics.tab.onboarding': 'Onboarding',
  'analytics.tab.interests': 'Interessen',
  'analytics.interests.snapshot': 'Aktuelle Interessenauswahl',
  'analytics.interests.scope': 'Alle Nutzer außer gelöschten Accounts. Gezeigt wird der aktuelle Profilstand, unabhängig vom Aktivitätszeitraum. Nutzer können mehrere Interessen auswählen.',
  'analytics.interests.updated': 'Stand: {date}',
  'analytics.interests.manage': 'Interessen verwalten',
  'analytics.interests.error': 'Die Interessen-Statistik konnte nicht geladen werden.',
  'analytics.interests.retry': 'Erneut versuchen',
  'analytics.interests.with': 'Nutzer mit Interessen',
  'analytics.interests.without': 'Nutzer ohne Interessen',
  'analytics.interests.coverage': '{rate} von {total} Nutzern',
  'analytics.interests.withoutHint': 'Keine Interessen im Profil ausgewählt',
  'analytics.interests.averageAll': 'Durchschnitt pro Nutzer',
  'analytics.interests.averageAllHint': 'Alle Nutzer, auch jene ohne Interessen',
  'analytics.interests.averageParticipants': 'Durchschnitt bei Angabe',
  'analytics.interests.averageParticipantsHint': 'Nur Nutzer mit mindestens einem Interesse',
  'analytics.interests.participation': 'Wie viele Nutzer geben Interessen an?',
  'analytics.interests.totalSelections': '{count} ausgewählte Interessen insgesamt',
  'analytics.interests.noUsers': 'Noch keine Nutzer vorhanden. Durchschnitt und Prozentwerte erscheinen, sobald Nutzer vorhanden sind.',
  'analytics.interests.participatingCount': '{count} Nutzer mit Interessen',
  'analytics.interests.missingCount': '{count} Nutzer ohne Interessen',
  'analytics.interests.popularity': 'Beliebteste Interessen',
  'analytics.interests.popularityHint': 'Nutzer pro Interesse, nach Beliebtheit sortiert. Prozentwerte beziehen sich auf alle Nutzer; Mehrfachauswahl ist möglich.',
  'analytics.interests.categories': 'Interessenkategorien',
  'analytics.interests.categoriesHint': 'Nutzer mit mindestens einem Interesse der Kategorie. Jeder Nutzer zählt pro Kategorie einmal; Prozentwerte beziehen sich auf alle Nutzer.',
  'analytics.interests.distribution': 'Wie viele Interessen wählen Nutzer?',
  'analytics.interests.distributionHint': 'Nutzer nach Anzahl ihrer ausgewählten Interessen. Prozentwerte beziehen sich auf alle Nutzer.',
  'analytics.interests.oneSelection': '1 Interesse',
  'analytics.interests.selectionCount': '{count} Interessen',
  'analytics.tab.marketplace': 'Marktplatz',
  'analytics.tab.communities': 'Communities',
  'analytics.tab.guidedTour': 'App-Tour',
  'analytics.loading': 'Analytics werden geladen…',
  'analytics.error': 'Analytics konnten nicht geladen werden',
  'analytics.noData': 'Für diesen Zeitraum sind keine Daten verfügbar.',
  'analytics.metric.totalUsers': 'Nutzer gesamt',
  'analytics.metric.newUsers': 'Neue Nutzer',
  'analytics.metric.activeUsers': 'Aktive Nutzer',
  'analytics.metric.completedTrips': 'Abgeschlossene Mitfahrten',
  'analytics.metric.onboardingRate': 'Onboarding-Abschluss',
  'analytics.metric.phoneCoverage': 'Handynummer angegeben',
  'analytics.phoneCoverage.count': '{count} von {total} neuen Nutzern',
  'analytics.metric.verificationRate': 'Verifizierungsquote',
  'analytics.verification.count': '{count} von {total} neuen Nutzern verifiziert',
  'analytics.metric.rideSearches': 'Fahrtsuchen',
  'analytics.activity.title': 'Wachstum und Aktivität',
  'analytics.activity.description': 'Tägliche eindeutige Aktivität im ausgewählten Zeitraum.',
  "analytics.push.title": "Push-Reichweite",
  "analytics.push.description": "Push-Registrierung der im gewählten Zeitraum aktiven Nutzer. All Time umfasst alle Nutzer. Jeder Nutzer zählt einmal. Ein gespeicherter Token bestätigt weder Zustellung noch Benachrichtigungsberechtigung.",
  "analytics.push.allUsers": "Alle Nutzer",
  "analytics.push.activeDays": "Aktiv in den letzten {days} Tagen",
  "analytics.push.audience": "Nutzergruppe",
  "analytics.push.users": "Nutzer",
  "analytics.push.registered": "Mit Push-Registrierung",
  "analytics.push.missing": "Ohne Registrierung",
  "analytics.push.coverage": "Abdeckung",
  "analytics.push.unknown": "Unbekannte Plattform",
  "analytics.push.platformHint": "Die Plattformspalten zählen Nutzer mit aktiver Push-Installation. Nutzer mit iOS und Android erscheinen in beiden Spalten. Ältere Profil-Tokens ohne aktive Installation zählen als unbekannte Plattform. Kategorie-Einstellungen werden nicht berücksichtigt.",
  "analytics.push.history": "Aufgezeichneter Verlauf",
  "analytics.push.historyMetric": "Verlaufskennzahl",
  "analytics.push.rate": "Abdeckung (%)",
  "analytics.push.count": "Registrierte Nutzer",
  "analytics.push.chart": "Verlauf der Push-Registrierung",
  "analytics.push.trackingSince": "Aufzeichnung seit {date} · Europe/Vienna. Je Tag bleibt der zuletzt erfasste Stand erhalten; heute wird stündlich aktualisiert. Fehlende Tage werden nicht rückwirkend rekonstruiert.",
  "analytics.push.pending": "Der erste Stand wird nach dem Start automatisch erfasst.",
  "analytics.push.noHistory": "Keine aufgezeichneten Werte im gewählten Zeitraum.",
  "analytics.push.historyDetails": "Erfasste Zahlen und Zeitpunkte anzeigen",
  "analytics.push.recordedAt": "Erfasst am (Europe/Vienna)",
  "analytics.push.updated": "Aktuelle Registrierung vom {date} · Europe/Vienna",
  "analytics.push.refresh": "Aktualisieren",
  "analytics.push.error": "Push-Reichweite konnte nicht geladen werden. Bitte erneut aktualisieren.",
  'analytics.platforms.title': 'App-Betriebssysteme',
  'analytics.platforms.description': 'Installationen mit einer App-Session im ausgewählten Zeitraum.',
  'analytics.platforms.knownInstallations': '{count} bekannte Installationen',
  'analytics.platforms.distribution': 'Betriebssystemverteilung: iOS {ios}, Android {android}',
  'analytics.dataSince': 'Sitzungs- und Suchdaten sind seit {date} verfügbar.',
  'analytics.users.funnel': 'Aktivierungs-Funnel neuer Nutzer',
  'analytics.users.funnelDescription': 'Aktueller Fortschritt der im ausgewählten Zeitraum registrierten Nutzer.',
  'analytics.users.acquisition': 'Akquisequelle',
  'analytics.users.mobilityRole': 'Mobilitätsrolle',
  'analytics.users.language': 'Sprache',
  'analytics.onboarding.open': 'Offene Onboardings',
  'analytics.onboarding.completion': 'Abschlussquote',
  'analytics.onboarding.biggest': 'Größter Abbruchpunkt',
  'analytics.onboarding.steps': 'Offene Nutzer nach Onboarding-Schritt',
  'analytics.onboarding.description': 'Erster unvollständiger Schritt bei Nutzern, die sich im gewählten Zeitraum registriert und ihr Onboarding noch nicht abgeschlossen haben.',
  'analytics.marketplace.zeroResults': 'Nulltrefferquote',
  'analytics.marketplace.requests': 'Fahrtgesuche',
  'analytics.marketplace.rides': 'Angebotene Fahrten',
  'analytics.marketplace.activity': 'Marktplatzaktivität',
  'analytics.marketplace.activityDescription': 'Fahrten außerhalb von Gruppen: Angebote nach Erstelldatum, gemeinsame Fahrten und Mitfahrten nach Abschlussdatum.',
  'analytics.marketplace.searchQuality': 'Such- und Buchungsqualität',
  'analytics.marketplace.successfulSearches': 'Suchen mit Ergebnissen',
  'analytics.marketplace.zeroResultSearches': 'Suchen ohne Ergebnisse',
  'analytics.marketplace.avgResults': 'Ergebnisse pro Suche',
  'analytics.marketplace.bookings': 'Erstellte Buchungen',
  'analytics.marketplace.cancelledBookings': 'Stornierte Buchungen',
  'analytics.communities.total': 'Communities',
  'analytics.communities.memberships': 'Aktuelle Mitgliedschaften',
  'analytics.communities.netGrowth': 'Netto-Mitgliederwachstum',
  'analytics.communities.byUsers': 'Communities nach Nutzern',
  'analytics.communities.byUsersDescription': 'Aktive Mitgliedschaften in den größten Communities.',
  'analytics.communities.growth': 'Aktuelles Wachstum',
  'analytics.communities.growthDescription': 'Netto-Mitgliederveränderung im ausgewählten Zeitraum.',
  'analytics.communities.details': 'Community-Wachstum im Detail',
  'analytics.managerVisits.title': 'Dashboard-Besuche der Manager',
  'analytics.managerVisits.description': 'Ein Besuch pro Öffnen des Dashboards. Filterwechsel und Wiederholungen zählen einmal. Zeitangaben: {timeZone}.',
  'analytics.managerVisits.visits': 'Dashboard-Besuche',
  'analytics.managerVisits.managers': 'Manager mit Besuchen',
  'analytics.managerVisits.byDay': 'Besuche nach Tag, Manager und Gemeinde',
  'analytics.managerVisits.day': 'Tag',
  'analytics.managerVisits.manager': 'Manager',
  'analytics.managerVisits.first': 'Erster Besuch',
  'analytics.managerVisits.last': 'Letzter Besuch',
  'analytics.managerVisits.recent': 'Letzte 100 Besuche im ausgewählten Zeitraum',
  'analytics.managerVisits.when': 'Datum und Uhrzeit',
  'analytics.managerVisits.empty': 'Keine erfassten Besuche im ausgewählten Zeitraum.',
  'analytics.communities.community': 'Community',
  'analytics.communities.current': 'Aktuelle Nutzer',
  'analytics.communities.period': 'Start → Ende',
  'analytics.communities.joined': 'Beigetreten',
  'analytics.communities.left': 'Ausgetreten',
  'analytics.communities.net': 'Netto',
  'analytics.communities.rate': 'Wachstum',
  'analytics.guidedTour.totalRuns': 'Tour-Starts',
  'analytics.guidedTour.uniqueUsers': 'Eindeutige Nutzer',
  'analytics.guidedTour.completionRate': 'Abschlussquote',
  'analytics.guidedTour.skipped': 'Übersprungen',
  'analytics.guidedTour.abandoned': 'Abgebrochen',
  'analytics.guidedTour.inProgress': 'Aktuell aktiv',
  'analytics.guidedTour.outcomes': 'Tour-Ergebnisse',
  'analytics.guidedTour.outcomesDescription': 'Abgeschlossene, übersprungene, abgebrochene und aktuell aktive Durchläufe.',
  'analytics.guidedTour.dropOffs': 'Abbruchschritte',
  'analytics.guidedTour.dropOffsDescription': 'Zuletzt erreichter Schritt bei übersprungenen oder abgebrochenen Touren.',
  'analytics.guidedTour.branches': 'Tour-Modi',
  'analytics.guidedTour.branchesDescription': 'Tour-Starts nach Fahrer- und Mitfahrmodus.',
  'analytics.value.REGISTERED': 'Registriert',
  'analytics.value.ONBOARDING_COMPLETED': 'Onboarding abgeschlossen',
  'analytics.value.SEARCHED': 'Nach Fahrt gesucht',
  'analytics.value.CREATED_RIDE_OR_REQUEST': 'Fahrt oder Gesuch erstellt',
  'analytics.value.COMPLETED_TRIP': 'Fahrt abgeschlossen',
  'analytics.value.NAME': 'Name & Geburtsdatum',
  'analytics.value.PROFILE': 'Profil',
  'analytics.value.TERMS': 'AGB',
  'analytics.value.SOURCE': 'Akquisequelle',
  'analytics.value.EVENT': 'Event',
  'analytics.value.COMMUNITY': 'Community',
  'analytics.value.INTERESTS': 'Interessen',
  'analytics.value.USAGE': 'Nutzungsart',
  'analytics.value.RIDE_PLAN': 'Fahrtplan',
  'analytics.value.VEHICLE': 'Fahrzeug',
  'analytics.value.VERIFICATION_CHOICE': 'Verifizierungsart',
  'analytics.value.VERIFICATION': 'Identitätsprüfung',
  'analytics.value.STRIPE': 'Stripe-Onboarding',
  'analytics.value.FINISH': 'Abschlussbestätigung',
  'analytics.value.OTHER': 'Sonstiges',
  'analytics.value.UNKNOWN': 'Nicht angegeben',
  'analytics.value.DRIVER': 'Fahrer',
  'analytics.value.RIDER': 'Mitfahrer',
  'analytics.value.BOTH': 'Fahrer und Mitfahrer',
  'analytics.value.COMPLETED': 'Abgeschlossen',
  'analytics.value.SKIPPED': 'Übersprungen',
  'analytics.value.ABANDONED': 'Abgebrochen',
  'analytics.value.IN_PROGRESS': 'Aktuell aktiv',
  'analytics.value.mode': 'Modusauswahl',
  'analytics.value.nearby': 'Fahrten in der Nähe',
  'analytics.value.locations': 'Gespeicherte Orte',
  'analytics.value.recurring': 'Regelmäßige Fahrten',
  'analytics.value.singleRide': 'Fahrt anlegen',
  'analytics.value.search': 'Fahrt suchen',
  'analytics.value.activity': 'Aktivitäten',
  'analytics.value.social': 'Social',
  'analytics.value.events': 'Events',
  'rides.title': 'Fahrten',
  'rides.description': 'Operative Ansicht für alle Fahrten, Buchungen, Fahrer, Zahlungen und Statusaktionen.',
  'rides.ongoing.loading': 'Laufende Fahrten werden geladen…',
  'rides.ongoing.count': '{count} laufende Fahrten',
  'rides.ongoing.count.one': '1 laufende Fahrt',
  'rides.filtersCleared': 'Fahrtfilter zurückgesetzt.',
  'rides.loading': 'Fahrten werden geladen…',
  'rides.found': '{count} Fahrten gefunden',
  'rides.searchPlaceholder': 'Fahrt-ID, Buchung-ID, Nutzer-ID, Name oder E-Mail suchen',
  'rides.filterByUser': 'Nach Nutzer filtern',
  'rides.allStatuses': 'Alle Status',
  'rides.sort.label': 'Fahrten sortieren',
  'rides.sort.time': 'Abfahrtsdatum',
  'rides.sort.createdAt': 'Erstelldatum',
  'rides.createdAt': 'Erstellt {date}',
  'rides.type.all': 'Alle Typen',
  'rides.type.single': 'Einzelfahrt',
  'rides.type.recurring': 'Serienfahrt',
  'rides.noMatches': 'Keine Fahrten für die aktuellen Filter.',
  'rides.status.scheduled': 'Geplant',
  'rides.status.started': 'Gestartet',
  'rides.status.completed': 'Abgeschlossen',
  'rides.status.cancelled': 'Storniert',
  'rides.status.expired': 'Abgelaufen',
  'rides.table.departureRoute': 'Abfahrt & Route',
  'rides.table.actions': 'Aktionen',
  'rides.table.ride': 'Fahrt',
  'rides.table.created': 'Erstellt',
  'rides.table.type': 'Typ',
  'rides.table.route': 'Route',
  'rides.table.driver': 'Fahrer',
  'rides.table.status': 'Status',
  'rides.table.bookings': 'Buchungen',
  'rides.table.payments': 'Zahlungen',
  'rides.bookingsCount': '{count} Buchungen',
  'rides.seatsApproved': '{approved}/{total} Sitze bestätigt',
  'rides.paymentIssues': '{count} Zahlungsproblem(e)',
  'rides.noPaymentIssues': 'Keine Zahlungsprobleme',
  'rides.details.loading': 'Fahrtdetails werden geladen…',
  'rides.details.id': 'Fahrt-ID',
  'rides.details.copyId': 'Fahrt-ID kopieren',
  'rides.action.expandInline': 'Details aufklappen',
  'rides.action.collapseInline': 'Details zuklappen',
  'rides.action.start': 'Starten',
  'rides.action.cancel': 'Stornieren',
  'rides.action.complete': 'Abschließen',
  'rides.action.openDetails': 'Details & Chat',
  'rides.action.working': 'Wird ausgeführt…',
  'ride.date': 'Datum',
  'ride.time': 'Uhrzeit',
  'ride.seats': 'Sitze',
  'rideRequest.title': 'Anfragen',
  'rideRequest.create': 'Fahrtanfrage erstellen',
  'rideRequest.departure': 'Abfahrt',
  'rideRequest.destination': 'Ziel',
  'rideRequest.status': 'Status',
  'rideRequest.status.requested': 'Angefragt',
  'rideRequest.status.approved': 'Genehmigt',
  'rideRequest.status.cancelled': 'Storniert',
  'rideRequest.status.finished': 'Beendet',
  'rideRequest.save': 'Fahrtanfrage speichern',
  'locations.title': 'Meine Orte',
  'locations.editTitle': 'Ort bearbeiten',
  'locations.createTitle': 'Neuen Ort erstellen',
  'locations.createNew': 'Neuer Ort',
  'locations.create': 'Ort erstellen',
  'locations.createSuccess': 'Ort erfolgreich erstellt',
  'locations.searchLocation': 'Nach Ort suchen',
  'locations.searchOrClickMap': 'Suchen Sie nach einem Ort oder klicken Sie auf die Karte',
  'locations.locationDetails': 'Ortsdetails',
  'locations.noLocations': 'Sie haben noch keine Orte gespeichert.',
  'locations.searchPlaceholder': 'Ort oder Adresse suchen…',
  'locations.select': 'Auswählen',
  'locations.selected': 'Ausgewählter Ort',
  'locations.save': 'Ort speichern',
  'locations.confirmDelete': 'Möchten Sie diesen Ort wirklich löschen?',
  'locations.deleteSuccess': 'Ort erfolgreich gelöscht',
  'locations.updateSuccess': 'Ort erfolgreich aktualisiert',
  'locations.isPrivate': 'Privatsphäre',
  'locations.private': 'Privat',
  'locations.public': 'Öffentlich',
  'locations.publicName': 'Öffentlicher Name',
  'locations.publicNamePlaceholder': 'z.B. Hauptstraße, Garching',
  'locations.publicNameDescription': 'Dieser Name ist für andere Nutzer sichtbar, wenn Sie Fahrten teilen.',
  'locations.privateName': 'Privater Name (optional)',
  'locations.privateNamePlaceholder': 'z.B. Zuhause, Arbeit, Fitnessstudio',
  'locations.privateNameDescription': 'Dieser Name ist nur für Sie sichtbar.',
  'locations.privateDescription': 'Bei privaten Orten wird Ihr benutzerdefinierter Name nur für Sie angezeigt.',
  'locations.coordinates': 'Koordinaten',
  'locations.longitude': 'Längengrad',
  'locations.latitude': 'Breitengrad',
  'locations.addToFavorites': 'Zu Favoriten hinzufügen',
  'locations.removeFromFavorites': 'Aus Favoriten entfernen',
  'locations.favorites': 'Favoriten',
  'directions.title': 'Navigation',
  'directions.from': 'Von',
  'directions.to': 'Nach',
  'directions.clear': 'Zurücksetzen',
  'directions.fetch': 'Route berechnen',
  'directions.distance': 'Strecke',
  'directions.duration': 'Dauer',
  'directions.alternatives': 'Alternativen',
  'ride.save': 'Fahrt speichern',
  'paymentOps.title': 'Zahlungen',
  'paymentOps.description': 'Prüfen Sie fehlgeschlagene Abbuchungen, Stripe-Connect-Routing-Lücken, offene PaymentIntents und Statuskonflikte ohne direkten Datenbankzugriff.',
  'paymentOps.searchPlaceholder': 'Buchung, Fahrt, PaymentIntent oder Nutzer suchen',
  'paymentOps.allIssues': 'Alle',
  'paymentOps.anyStatus': 'Jeder Status',
  'paymentOps.loading': 'Lädt…',
  'paymentOps.bookings': '{count} Buchungen',
  'paymentOps.metric.allBookings': 'Alle Buchungen',
  'paymentOps.metric.authorized': 'Autorisiert',
  'paymentOps.metric.pending': 'Ausstehend',
  'paymentOps.metric.captured': 'Abgebucht',
  'paymentOps.metric.cancelled': 'Storniert',
  'paymentOps.metric.notRequired': 'Nicht erforderlich',
  'paymentOps.metric.captureFailed': 'Abbuchung fehlgeschlagen',
  'paymentOps.metric.notCaptured': 'Nicht abgebucht',
  'paymentOps.metric.noConnectRouting': 'Kein Connect-Routing',
  'paymentOps.metric.connectIncomplete': 'Connect unvollständig',
  'paymentOps.metric.connectStale': 'Connect veraltet',
  'paymentOps.metric.openIntents': 'Offene Intents',
  'paymentOps.metric.stateMismatch': 'Statuskonflikt',
  'paymentOps.metric.missingIntent': 'Intent fehlt',
  'paymentOps.noMatches': 'Keine Buchungen für die aktuellen Filter.',
  'paymentOps.retryCapture': 'Abbuchung erneut versuchen',
  'paymentOps.retrying': 'Erneut versuchen…',
  'paymentOps.cancelAuth': 'Autorisierung stornieren',
  'paymentOps.confirmRetry': 'Abbuchung für diese Buchung erneut versuchen?',
  'paymentOps.confirmCancelAuth': 'Stripe-Autorisierung für diese inaktive Buchung stornieren? Dies erstattet keine bereits abgebuchten Zahlungen.',
  'paymentOps.retryFinished': 'Abbuchung erneut ausgeführt.',
  'paymentOps.retryFailed': 'Abbuchung konnte nicht erneut ausgeführt werden',
  'paymentOps.authCancelled': 'Autorisierung storniert.',
  'paymentOps.authCancelFailed': 'Autorisierung konnte nicht storniert werden',
  'paymentOps.issue.ALL': 'Alle',
  'paymentOps.issue.CAPTURE_FAILED': 'Abbuchung fehlgeschlagen',
  'paymentOps.issue.COMPLETED_NOT_CAPTURED': 'Abgeschlossen, nicht abgebucht',
  'paymentOps.issue.CAPTURED_WITHOUT_CONNECT_ROUTING': 'Abgebucht ohne Connect-Routing',
  'paymentOps.issue.DRIVER_CONNECT_INCOMPLETE': 'Driver Connect unvollständig',
  'paymentOps.issue.DRIVER_CONNECT_IN_REVIEW': 'Driver Connect in Prüfung',
  'paymentOps.issue.CONNECT_STATUS_STALE': 'Connect-Status veraltet',
  'paymentOps.issue.INACTIVE_WITH_OPEN_INTENT': 'Inaktiv mit offenem Intent',
  'paymentOps.issue.STATE_MISMATCH': 'Statuskonflikt',
  'paymentOps.issue.PAYMENT_INTENT_MISSING': 'Intent fehlt',
  'assistant.prompt.findRide': 'Finde mir morgen früh eine Fahrt vom Wiener Hauptbahnhof nach Graz.',
  'assistant.prompt.showBookings': 'Zeige meine aktiven Buchungen.',
  'assistant.prompt.bookRide': 'Ich möchte am Freitag um 17:00 Uhr eine Fahrt von Linz nach Salzburg buchen.',
  'assistant.error.initialize': 'Assistent konnte nicht initialisiert werden',
  'assistant.error.send': 'Nachricht konnte nicht gesendet werden',
  'assistant.brand': 'Mobie KI',
  'assistant.title': 'Assistent für Fahrtenbuchung',
  'assistant.description': 'Suche Fahrten, prüfe Schätzungen, liste Buchungen auf und bereite Buchungen oder Stornierungen im Chat vor. Das Backend hält alle Fahrtdaten tool-gestützt und bestätigt Änderungen erst nach Freigabe.',
  'assistant.session': 'Sitzung',
  'assistant.session.active': 'Aktiv: {id}…',
  'assistant.session.creating': 'Chat-Sitzung wird erstellt…',
  'assistant.session.description': 'Dies ist der textbasierte Agentenfluss. Dasselbe Backend-Sitzungsmodell kann später auch Sprache unterstützen.',
  'assistant.suggestedPrompts': 'Vorgeschlagene Prompts',
  'assistant.confirmationHelp': 'Bestätigungshilfe',
  'assistant.confirmationHint': 'Wenn der Assistent eine Buchung oder Stornierung vorbereitet, antworten Sie bitte klar mit Ja oder Nein.',
  'assistant.channel': 'Chat-Kanal',
  'assistant.chatTitle': 'KI-Buchungschat',
  'assistant.status.initializing': 'Initialisiert',
  'assistant.status.thinking': 'Denkt nach',
  'assistant.status.ready': 'Bereit',
  'assistant.chatDescription': 'Die Admin-App spricht jetzt mit der Backend-API für Agentensitzungen. Das Backend ruft wiederum das LLM auf und stellt ihm nur einen festen Satz an Fahrt-Tools zur Verfügung.',
  'assistant.initializingSession': 'Neue Assistenten-Sitzung wird erstellt…',
  'assistant.empty.eyebrow': 'Leere Unterhaltung',
  'assistant.empty.title': 'Starten Sie mit einer Frage zu Fahrten',
  'assistant.empty.description': 'Fragen Sie nach einer Fahrt, einer Preisschätzung oder Ihren aktuellen Buchungen. Wenn der Assistent eine Änderung vorbereitet, antworten Sie einfach mit Ja oder Nein.',
  'assistant.message.you': 'Sie',
  'assistant.typing': 'Mobie KI prüft die Fahrt-Tools…',
  'assistant.inputPlaceholder': 'Bitten Sie um eine Fahrtsuche, eine Abholschätzung, eine Buchung oder zeigen Sie Ihre aktiven Buchungen an…',
  'assistant.footerHint': 'Der Assistent kann Buchungen und Stornierungen vorbereiten, benötigt dafür aber weiterhin Ihre abschließende Ja/Nein-Bestätigung.',
  'assistant.action.sending': 'Wird gesendet…',
  'assistant.action.send': 'Nachricht senden',
};

const dictionaries: Record<Language, Record<string, string>> = {
  en: { ...en, ...usersEn, ...shellEn, ...communitiesEn, ...rideFiltersEn, ...optimizerEn, ...voiceCallsEn },
  de: { ...de, ...usersDe, ...shellDe, ...communitiesDe, ...rideFiltersDe, ...optimizerDe, ...voiceCallsDe },
};

function interpolate(template: string, values?: Record<string, string | number>) {
  if (!values) return template;
  return template.replace(/\{(.*?)\}/g, (_, k) => String(values[k] ?? ''));
}

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = React.useState<Language>(() => {
    const stored = localStorage.getItem('lang');
    return (stored === 'de' || stored === 'en') ? (stored as Language) : 'en';
  });

  React.useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const t = React.useCallback((key: string, values?: Record<string, string | number>) => {
    const dict = dictionaries[language] || dictionaries.en;
    const template = dict[key] ?? key;
    return interpolate(template, values);
  }, [language]);

  const setLang = React.useCallback((lang: Language) => {
    localStorage.setItem('lang', lang);
    setLanguage(lang);
  }, []);

  const value = React.useMemo<I18nContextValue>(() => ({ language, setLanguage: setLang, t }), [language, setLang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export function useI18n() {
  const ctx = React.useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
}
